import { IconButton } from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useId } from 'react';
import type { Project } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';

export interface CustomHeaderProps {
  project: Project;
  fallback: React.ReactNode;
  /** Id for the header's visible title, which names the tab panel region. */
  headingId: string;
}

interface Props {
  project: Project;
  tabId: string;
  tabName: React.ReactNode;
  backButtonPath?: string;
  CustomHeader?: ((props: CustomHeaderProps) => React.ReactNode) | null;
  /** Fills the main area with no map beside it. */
  fullWidth?: boolean;
  /**
   * Wraps the content in a scroller; full-width panels always get one. Other tabs scroll themselves,
   * so wrapping them would nest scrollbars.
   */
  scrollsOwnContent?: boolean;
  className?: string;
  children: React.ReactNode;
}

/** The open project tab: a region named by its header title, followed by the tab content. */
export default function TabPanel({
  project,
  tabId,
  tabName,
  backButtonPath,
  CustomHeader,
  fullWidth = false,
  scrollsOwnContent = false,
  className,
  children,
}: Props) {
  const navigate = useNavigate();
  const headingId = useId();
  const projectId = project.id;

  const defaultHeader = (
    <div className={cn('mb-1 flex items-center justify-between', fullWidth ? 'shrink-0 p-4 pb-2' : 'py-2')}>
      <div className="flex items-center space-x-2">
        {backButtonPath && (
          <IconButton
            icon="chevronLeft"
            onClick={() =>
              navigate({
                to: '/projects/$projectId/$tabId',
                params: { projectId, tabId: backButtonPath },
              })
            }
            variant="hollow"
            size="icon"
            aria-label="Back"
            data-testid="project-detail-subtab-back"
          />
        )}
        <h2 id={headingId} className={cn('text-lg font-medium', backButtonPath ? 'cursor-pointer' : '')}>
          {tabName}
        </h2>
      </div>

      <div className="flex items-center space-x-2">
        <CloseIconButton
          onClick={() => navigate({ to: '/projects/$projectId', params: { projectId } })}
          aria-label={typeof tabName === 'string' && tabName ? `Close ${tabName}` : 'Close panel'}
          data-testid="project-detail-subtab-close"
        />
      </div>
    </div>
  );

  return (
    <section
      id={`tab-${tabId}-open`}
      aria-labelledby={headingId}
      data-testid="project-detail-tab-panel"
      className={cn(fullWidth ? 'flex w-full flex-col overflow-hidden' : 'flex min-w-0 grow flex-col', className)}
    >
      {CustomHeader ? <CustomHeader project={project} fallback={defaultHeader} headingId={headingId} /> : defaultHeader}

      {fullWidth || scrollsOwnContent ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-auto">{children}</div>
      ) : (
        children
      )}
    </section>
  );
}
